'use client';

import React from 'react';

const destinations = [
{
  name: 'Mediterranean',
  tagline: 'Santorini · Amalfi · Monaco',
  image: "https://images.unsplash.com/photo-1662121783914-9ee843f21504",
  alt: 'Aerial view of Santorini white buildings and blue domes overlooking the Mediterranean Sea',
  duration: '7–14 nights',
  from: 'From $8,500',
  badge: 'Most Popular'
},
{
  name: 'Norwegian Fjords',
  tagline: 'Bergen · Flåm · Geiranger',
  image: "https://images.unsplash.com/photo-1685870968328-cbe12eb2347c",
  alt: 'Dramatic Norwegian fjord with steep cliffs and calm blue water reflecting mountains',
  duration: '10–12 nights',
  from: 'From $11,200',
  badge: null
},
{
  name: 'Caribbean Islands',
  tagline: 'St. Barts · Barbados · Antigua',
  image: "https://images.unsplash.com/photo-1715579318876-aefd7643db08",
  alt: 'Luxury cruise ship sailing through turquoise Caribbean waters with tropical islands in background',
  duration: '7–10 nights',
  from: 'From $7,200',
  badge: null
},
{
  name: 'Antarctica',
  tagline: 'Drake Passage · Weddell Sea',
  image: "https://images.unsplash.com/photo-1563377226776-1b0bd48458f2",
  alt: 'Expedition ship navigating through icy Antarctic waters with massive icebergs and penguins on ice',
  duration: '14–21 nights',
  from: 'From $18,000',
  badge: 'Expedition'
},
{
  name: 'Asia & Far East',
  tagline: 'Tokyo · Singapore · Bali',
  image: "https://images.unsplash.com/photo-1705056132012-246a7b5cb4b7",
  alt: 'Illuminated Tokyo skyline at night with Mount Fuji visible in the background',
  duration: '12–18 nights',
  from: 'From $9,800',
  badge: null
},
{
  name: 'South Pacific',
  tagline: 'Fiji · Tahiti · Bora Bora',
  image: "https://images.unsplash.com/photo-1706461168280-da6986cd91ec",
  alt: 'Overwater bungalows in Bora Bora with crystal clear turquoise lagoon and lush green mountains',
  duration: '10–14 nights',
  from: 'From $12,500',
  badge: 'Exclusive'
}];


export default function CruisesSection() {
  return (
    <section id="cruises" className="py-28 bg-navy-dark relative overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5"
      style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(201,169,110,0.5) 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }} />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-10 bg-gold" />
              <span className="text-gold text-xs tracking-[0.3em] uppercase font-light">Destinations</span>
            </div>
            <h2 className="font-serif text-white text-4xl md:text-5xl font-light leading-tight">
              Where Will You{' '}
              <span className="italic text-gold">Sail Next?</span>
            </h2>
          </div>
          <a
            href="https://luxury-travel-club.cruiseco.com.au/"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 text-white/50 hover:text-gold text-sm tracking-[0.1em] uppercase font-light transition-colors duration-300">

            View All Cruises
            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>

        {/* Grid — varied sizes */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* Large featured card */}
          <div className="md:col-span-7 relative rounded-sm overflow-hidden group h-[420px] bg-navy">

            <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105" style={{ backgroundImage: `url(${destinations?.[0]?.image})` }} />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-dark via-navy-dark/40 to-transparent" />
            {destinations?.[0]?.badge &&
            <div className="absolute top-4 left-4 px-3 py-1 bg-gold text-navy-dark text-xs tracking-[0.15em] uppercase font-medium rounded-sm">
                {destinations?.[0]?.badge}
              </div>
            }
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <p className="text-gold/70 text-xs tracking-[0.2em] uppercase mb-1">{destinations?.[0]?.tagline}</p>
              <h3 className="font-serif text-white text-3xl font-light mb-3">{destinations?.[0]?.name}</h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-white/50 text-sm">{destinations?.[0]?.duration}</span>
                </div>
                <a
                  href="https://luxury-travel-club.cruiseco.com.au/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white hover:border-gold hover:text-gold transition-all duration-300 group/btn">

                  <svg className="w-4 h-4 group-hover/btn:translate-x-0.5 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Two stacked cards */}
          <div className="md:col-span-5 flex flex-col gap-4">
            {[destinations?.[1], destinations?.[2]]?.map((dest) =>
            <div key={dest?.name} className="relative rounded-sm overflow-hidden group h-[200px] bg-navy">

                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105" style={{ backgroundImage: `url(${dest?.image})` }} />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-dark via-navy-dark/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-gold/70 text-xs tracking-[0.15em] uppercase mb-0.5">{dest?.tagline}</p>
                  <div className="flex items-center justify-between">
                    <h3 className="font-serif text-white text-xl font-light">{dest?.name}</h3>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Three equal cards */}
          {destinations?.slice(3)?.map((dest) =>
          <div key={dest?.name} className="md:col-span-4 relative rounded-sm overflow-hidden group h-[260px] bg-navy">

              <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105" style={{ backgroundImage: `url(${dest?.image})` }} />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-dark via-navy-dark/40 to-transparent" />
              {dest?.badge &&
            <div className="absolute top-4 left-4 px-3 py-1 bg-gold/90 text-navy-dark text-xs tracking-[0.15em] uppercase font-medium rounded-sm">
                  {dest?.badge}
                </div>
            }
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-gold/70 text-xs tracking-[0.15em] uppercase mb-0.5">{dest?.tagline}</p>
                <div className="flex items-center justify-between">
                  <h3 className="font-serif text-white text-xl font-light">{dest?.name}</h3>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Member discount callout */}
        <div className="mt-8 p-6 border border-gold/20 rounded-sm flex flex-col sm:flex-row items-center justify-between gap-4 bg-gold/5">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
              <svg className="w-5 h-5 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </div>
            <p className="text-white/70 font-light">
              <span className="text-gold font-medium">Members receive an exclusive members only discount</span> on every cruise listed above
            </p>
          </div>
          <a
            href="#join"
            className="whitespace-nowrap px-6 py-2.5 bg-gold-gradient text-navy-dark text-sm font-medium tracking-[0.1em] uppercase rounded-sm hover:shadow-lg hover:shadow-gold/20 transition-all duration-300">

            Join to Save
          </a>
        </div>
      </div>
    </section>);

}